import { cva } from 'class-variance-authority'
import { type icons, AlertTriangle, Asterisk } from 'lucide-react'
import * as React from 'react'
import { type SetStateAction, type Dispatch } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from '../tiptap/ui/Icon'

const inputVariants = cva(
	'flex w-full border bg-primary-foreground text-sm outline-none transition-all duration-150 ease-in-out file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-secondary/20 hover:shadow-button shadow-sm focus-visible:border-secondary focus-visible:shadow focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid',
	{
		variants: {
			variant: {
				default: 'h-[45px] w-full rounded-lg px-4',
				s: 'h-8 w-full rounded-md px-3',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

interface InputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'variant'> {
	variant?: 'default' | 's'
	icon?: keyof typeof icons | null
	iconText?: string
	required?: boolean
	unique?: boolean
	noIcon?: boolean
	error?: string
	setError?: Dispatch<SetStateAction<string>>
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			type,
			className,
			variant = 'default',
			icon,
			iconText,
			required,
			noIcon = false,
			unique = false,
			error,
			setError,
			...props
		},
		ref,
	) => {
		let padding = 16
		let radius = 10

		switch (variant) {
			case 's':
				padding = 8
				radius = 8
				break
			default:
				padding = 16
				radius = 10
		}

		return (
			<div className="relative">
				<input
					type={type}
					className={cn(
						inputVariants({ variant, className }),
						(icon || iconText) && (variant === 's' ? 'pl-8' : 'pl-12'),
					)}
					onFocus={setError && (() => setError(''))}
					onChange={setError && (() => setError(''))}
					aria-errormessage={error}
					required={required}
					ref={ref}
					{...props}
					name={unique && type !== 'email' ? `#_${props.name}` : props.name}
				/>
				{(icon || iconText) && (
					<div
						className="absolute inset-y-[1px] left-[1px] flex items-center justify-center"
						style={{
							borderRadius: `0px ${radius}px ${radius}px 0px`,
							padding: `0 ${padding}px`,
						}}
					>
						{iconText && (
							<div className="flex h-full w-4 shrink-0 items-center justify-center text-xs font-medium leading-none">
								<p className="m-0 p-0 leading-none">{iconText}</p>
							</div>
						)}
						{icon && <Icon name={icon} className="h-4 w-4 shrink-0" />}
					</div>
				)}
				{required && !noIcon && !error && (
					<div className="absolute right-3 top-1/2 -translate-y-1/2">
						<Asterisk width={16} className="input-tt-text" />
					</div>
				)}
				{error && (
					<div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
						<AlertTriangle width={16} className="input-tt-text" />
					</div>
				)}
			</div>
		)
	},
)
Input.displayName = 'Input'

export { Input }
